import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PagePagination } from "@/components/ui/page-pagination";
import { ChangeEvent, useMemo, useState } from "react";
import { User, useOrganizationUsersQuery } from "@common/utils/graphql";
import { DataTable } from "@common/components/Table/DataTable";
import { peopleTabColumns } from "./PeopleTabColumns";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { InvitePersonSheet } from "./InvitePersonSheet";
import { ViewPersonSheet } from "./ViewPersonSheet";
import { useSetRecoilState } from "recoil";
import { viewPersonManager } from "./ViewPersonContent";
import { Card } from "@/components/ui/card";

const PAGE_SIZE = 10;

export const PeopleTab = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [searchText, setSearchText] = useState("");

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const closeInviteModal = () => setIsInviteOpen(false);
  const setViewPersonManager = useSetRecoilState(viewPersonManager);

  const [viewPerson, setViewPerson] = useState<User | undefined>();
  const resetPerson = () => setViewPerson(undefined);

  const {
    loading: loadingUsers,
    data: usersDataRaw,
    refetch: refetchUsers,
  } = useOrganizationUsersQuery({
    variables: {
      offset: currentPage * PAGE_SIZE,
      pageSize: PAGE_SIZE,
      name: searchText,
    },

    fetchPolicy: "no-cache",
  });

  const userData = useMemo(() => {
    if (
      usersDataRaw?.organizationUsers?.__typename !==
      "QueryOrganizationUsersSuccess"
    ) {
      return {
        hasMore: false,
        users: [],
      };
    }
    return usersDataRaw.organizationUsers.data;
  }, [usersDataRaw]);

  const onBack = () => {
    setIsInviteOpen(false);
    refetchUsers();
  };

  const handleSearchOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setCurrentPage(0);
  };

  const header = (
    <div className="flex gap-5 pt-5">
      <Input
        className="w-full"
        type="search"
        placeholder="Search"
        value={searchText}
        onChange={handleSearchOnChange}
      />
      <Button onClick={() => setIsInviteOpen(true)}>Invite People</Button>
    </div>
  );

  const body = (
    <>
      <Card className="mt-5">
        <DataTable
          columns={peopleTabColumns}
          data={userData.users as User[]}
          onClick={(row) => {
            setViewPerson(row.original);
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            setViewPersonManager(row.original.employee?.manager! as User);
          }}
        />
      </Card>
      {!loadingUsers && userData.users.length > 0 && (
        <PagePagination
          currentPage={currentPage}
          hasMorePages={userData.hasMore}
          onChange={setCurrentPage}
        />
      )}
    </>
  );

  const sheets = (
    <>
      <InvitePersonSheet
        isOpen={isInviteOpen}
        onClose={closeInviteModal}
        onBack={onBack}
      />
      <ViewPersonSheet
        isOpen={!!viewPerson}
        onClose={resetPerson}
        person={viewPerson!}
      />
    </>
  );

  return (
    <>
      {header}
      {loadingUsers && userData.users.length === 0 ? <FullPageLoader /> : body}
      {sheets}
    </>
  );
};
